import type { Image, ImageSource, LocationListType, Price } from '../constants/constants';

declare global {
  interface Window {
    dataLayer: any[];
    utag_data: any;
    utag: any;
  }
}

export const renderStringAsHtml = <T>(htmlString: string, isList = false): T => {
  const domParser = new DOMParser();
  const htmlItem = domParser.parseFromString(htmlString, 'text/html');

  if (isList) {
    return htmlItem.body.childNodes as T;
  }
  return htmlItem.body.childNodes[0] as T;
};

export const formatPrice = (price: Price, withCurrency: boolean = false): string => {
  const currency = withCurrency ? ' ' + price.currency : '';
  return price ? ((price.amount * 0.01).toFixed(2) + currency) : '';
}

export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);  // deg2rad below
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  ;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in km
  return d;
};

const deg2rad = (deg: number) => {
  return deg * (Math.PI / 180);
};

export const copyToClipboard = (text: string) => {
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

interface FormDataObject {
  [key: string]: string[];
}

export const getScrollBarWidth = (): number =>
  parseInt(document.body.style.getPropertyValue('--scrollbar-width'), 10);

export const isMobileView = (): boolean => (window.innerWidth + getScrollBarWidth()) < 577;

export const getFormData = (form: HTMLFormElement): FormDataObject => {
  const requestBody: FormDataObject = {};
  for (const [key, value] of new FormData(form).entries()) {
    const cleanedKey = key.replace('[]', '');
    if (!requestBody[cleanedKey]) {
      requestBody[cleanedKey] = [];
    }
    requestBody[cleanedKey].push(value as string);
  }

  return requestBody;
}

export const dataLayerPush = (type: string, value: string, eventType: string = 'click'): void => {
  if (window.dataLayer && type) {
    let dataLayerData = {
      'event': eventType,
      'type': type,
      'value': value,
    };
    window.dataLayer.push(dataLayerData);
  }
};

export const createCustomEvent = (elem: HTMLElement, eventName: string, eventData?: {}): void => {
  const event = new CustomEvent(eventName, eventData);
  elem.dispatchEvent(event);
}

export const stringToLocationList = (markerString: string): LocationListType[] => {
  if (markerString.length > 0) {
    return markerString.split('|').map((markerSet) => {
      const [id, lat, lng, title, slug] = markerSet.split(',');
      if (slug === undefined) {
        console.error('slug is undefined in list of locations');
      }
      return {id: parseInt(id), lat: parseFloat(lat), lng: parseFloat(lng), title, slug};
    });
  }

  return [];
};

export const presetInputByUrlParams = (inputList: NodeListOf<HTMLInputElement>, callBack?: () => void) => {
  const href = new URL(window.location.href);
  inputList.forEach((input) => {
    const filterName = input.getAttribute('name') ?? '';
    const filterValue = input.getAttribute('value') ?? '';
    const urlValuesByKey = href.searchParams.getAll(filterName);

    const isUrlParamValueExisting = urlValuesByKey.filter((urlVal) => {
      return urlVal?.includes(filterValue);
    }).length > 0;

    if (isUrlParamValueExisting) {
      if (input.type === 'checkbox' || input.type === 'radio') {
        input.checked = true;
      }

      if (input.type === 'text' || input.type === 'search' || input.type === 'hidden') {
        input.value = urlValuesByKey[urlValuesByKey.length - 1];
      }
    }
  });

  if (callBack) {
    callBack();
  }
};

export const setFilterInUrlParam = (formEl: HTMLFormElement, pruneParams: boolean = false, ignoreKeys: string[] = ['page', 'sort', 'order', 'limit', 'offset', 'productCategory']) => {
  setTimeout(() => {
    const href = new URL(window.location.href);
    const formData = new FormData(formEl);
    const formMap = new Map<string, string[]>();

    // prepare Form data and put it in a map
    for (const [key, value] of formData.entries()) {
      if (!formMap.has(key)) {
        formMap.set(key, []);
      }
      formMap.get(key)!.push(value.toString());
    }

    // Delete all values from url which are not in form values
    for (const key of href.searchParams.keys()) {
      const urlValuesByKey = href.searchParams.getAll(key);
      const formValuesByKey = formMap.get(key);
      // if there is key in the url which does not exist in the form, then delete it
      const urlLacksFormValue = urlValuesByKey.filter((urlVal) => {
        return formValuesByKey?.includes(urlVal);
      }).length === 0;

      const isNewValueEmpty = formValuesByKey && formValuesByKey[0]?.length === 0 && urlValuesByKey[0].length > 0;
      if (!formMap.has(key) || !urlLacksFormValue || pruneParams || isNewValueEmpty) {
        // remove this key from url (is for singe value and array value)
        href.searchParams.delete(key);
      }
    }

    for (const [key, formValue] of formMap.entries()) {
      // Only add allowed values if they are not empty and pruneParams is not true
      if (!ignoreKeys.includes(key) && formValue.length && !pruneParams) {
        // Otherwise add all values by the following rules
        formValue.forEach(value => {
          const propertyIsArray = key.endsWith('[]');
          const keyNotExistInUrl = !href.searchParams.has(key);
          const urlValuesByKey = href.searchParams.getAll(key);
          const valueNotExistInUrl = urlValuesByKey.filter(e => e === value).length === 0;

          // Modified condition: For non-array properties, always update the value
          // For array properties, only add if the value doesn't exist
          if (value && ((!propertyIsArray) || (propertyIsArray && valueNotExistInUrl))) {
            // For non-array properties, delete existing values first to avoid duplicates
            if (!propertyIsArray && !keyNotExistInUrl) {
              href.searchParams.set(key, value);
            } else {
              href.searchParams.append(key, value);
            }
          }
        });
      }
    }

    window.history.replaceState(
      {additionalInformation: document.head.title},
      document.head.title,
      href.href
    );
  }, 200);
};

export const createItemImage = (itemImage: Image, imageClasses: string, imageTitle: string = ''): string => {
  if(!itemImage){
    return '';
  }

  return `
  <picture class="${imageClasses}">
    <source type="image/webp" class="img-fluid"
        src="${itemImage.source.x1}"
        width="${itemImage.config.width}"
        height="${itemImage.config.height}"
        alt="${imageTitle === '' ? itemImage.title : imageTitle}"
        srcset="${builtSrcSet(itemImage.source)}">
    <img
        type="image/jpg"
        class="img-fluid"
        src="${itemImage.jpeg}"
        width="${itemImage.config.width}"
        height="${itemImage.config.height}"
        alt="${imageTitle === '' ? itemImage.title : imageTitle}" />
    <div class="fallback"></div>
  </picture>`
};

const builtSrcSet = (source: ImageSource): string =>
  `${source.x1 ? source.x1 + ' 1x' : ''}${source.x1_5 ? ', ' + source.x1_5 + ' 1.5x ' : ''}${source.x2 ? ', ' + source.x2 + ' 2x ' : ''}`;

export const setHash = (hash: string = ''): void => {
  history.pushState('', document.title, window.location.pathname
    + window.location.search + '#' + hash);
}

export const createDeferred = <T>() => {
  let resolve!: (value: T | PromiseLike<T>) => void;
  let reject!: (reason?: any) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}
